import type { ReactNode } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.js';
import { env } from '../env.js';

interface LayoutProps {
  children?: ReactNode;
}

const NAV_ITEMS = [
  { to: '/ninas', label: 'Niñas' },
  { to: '/actividades', label: 'Actividades' },
  { to: '/adelantos', label: 'Adelantos' },
  { to: '/capacitaciones', label: 'Capacitaciones' },
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/admin', label: 'Admin' },
] as const;

function UserBadge() {
  const { state, logout } = useAuth();
  if (state.status !== 'authenticated') {
    return <span className="hidden sm:inline text-distrito-100">Modo: {env.MODE}</span>;
  }
  return (
    <div className="flex items-center gap-3">
      <div className="text-right hidden sm:block">
        <div className="text-sm font-medium text-white">{state.user.email}</div>
        <div className="text-xs text-distrito-100">{state.user.rol}</div>
      </div>
      <button
        type="button"
        onClick={logout}
        className="px-3 py-1.5 text-sm bg-white/15 hover:bg-white/25 rounded-md text-white border border-white/30 transition-colors"
      >
        Cerrar sesión
      </button>
    </div>
  );
}

export function Layout({ children }: LayoutProps) {
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <header className="bg-gradient-to-r from-distrito-600 to-accent-500 text-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-90">
            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-distrito-600 font-bold shadow-sm">
              S
            </div>
            <div className="leading-tight">
              <div className="font-semibold text-lg">SISREP Distrital</div>
              <div className="text-distrito-100 text-xs">{env.DISTRITO_ID}</div>
            </div>
          </Link>
          <UserBadge />
        </div>
        <nav className="bg-distrito-700/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ul className="flex flex-wrap gap-1 -mb-px">
              {NAV_ITEMS.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `inline-block px-3 py-2 text-sm transition-colors ${
                        isActive
                          ? 'bg-distrito-800 text-white border-b-2 border-accent-300'
                          : 'text-distrito-100 hover:bg-distrito-800 hover:text-white'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </nav>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children ?? <Outlet />}
      </main>

      <footer className="bg-gray-100 border-t border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-sm text-gray-600 flex flex-wrap justify-between gap-2">
          <span>© 2026 Distrito Guías de México — Monterrey Cumbres</span>
          <span className="font-mono text-xs">SISREP v0.1.0</span>
        </div>
      </footer>
    </div>
  );
}
